import { createEffect } from '@ngrx/effects'
import * as rxjs from 'rxjs'

import type { Action } from '../actions'
import type { Feature } from '../feature'

import { type Effect, SKIP } from './effect'

/**
 * Emits every {@link Action} from every {@link Effect} submitted using
 * {@link submitThroughMetaEffect}
 */
const ACTION$ = new rxjs.Subject<Action.Constraint>()
const SUBSCRIPTIONS: rxjs.Subscription[] = []

/**
 * Single effect that combines all dynamically registered effects
 *
 * @see {@link submitThroughMetaEffect}
 */
export const META_EFFECT = createEffect(
  () =>
    new rxjs.Observable<Action>(subscriber => {
      // Relays the subscription to ACTION$
      const actionSubscription = ACTION$.subscribe(subscriber)
      // Handles unsubscribing from every subscription used to support this
      // Observable
      return () => {
        // TODO: The following line causes issues in tests. If runtime problems
        // arise, revisit this. For now, keep it commented out.
        // ACTION$.unsubscribe()
        actionSubscription.unsubscribe()
        for (const subscription of SUBSCRIPTIONS) {
          subscription.unsubscribe()
        }
      }
    }),
  { functional: true },
)

/**
 * Sends an effect to the {@link META_EFFECT}
 *
 * __NOTE:__ this should generally not be used directly. Instead, use
 * {@link Feature.registerEffects}
 */
export function submitThroughMetaEffect(effect: Effect): void {
  // NOTE: it is important that the subscription happens synchronously in here
  // or else weird behavior can occur in certain edge cases (e.g. when using
  // defer w/ a Promise)
  SUBSCRIPTIONS.push(
    effect.subscribe(action => {
      if (action === SKIP) {
        // Nothing to do
        return
      }
      ACTION$.next(action)
    }),
  )
}
